// Properties data layer (erp-core 2A-23, `GET /v1/erp/hotel/properties`, keyed
// `branches`; X-Branch-Id = the caller's current property). The server returns
// the screen as data (rule 7): the totals «٧٧ غرفة · إشغال ٩٢٪», one row per
// property the caller may see (the owner: all; a property manager: their own
// only), the static shared / per-property list and the permission matrix —
// the web renders them and computes nothing. `current` is the property of the
// caller's X-Branch-Id; a switch is `switchBranch` of the shell (every query
// resets, so the next read says the new `current`).

import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useErp } from '@/app/erp-context';
import { erpApi } from '@/lib/api';
import { hotelKeys } from '../api';

/** One property card: the branch plus today's figures (occupancy, ADR, today's posted room + extras revenue). */
export interface PropertyRow {
  branch_id: string;
  name: string;
  city: string;
  /** The main branch is the server default (no X-Branch-Id needed to open it). */
  is_main: boolean;
  /** All physical rooms — the switcher's «كربلاء · ٥٣». */
  rooms: number;
  /** Rooms that are not suites + suites = `rooms` (the card's «٤٨ غرفة + ٥ أجنحة»). */
  standard_rooms: number;
  suites: number;
  /** The caller's X-Branch-Id (absent header = main). */
  current: boolean;
  /** Today's occupancy, whole percent. */
  pct: number;
  adr_minor: number;
  revenue_today_minor: number;
}

/** One row of the «مشترك بين العقارات» list; `scope_label` is the pill word the server chose. */
export interface SharedRow {
  id: string;
  label: string;
  scope: 'shared' | 'per_property';
  scope_label: string;
}

/**
 * A matrix cell (ADR-0029 `matrix:[…]`, an ARRAY of rows): `yes` = a check,
 * `no` = «—», `text` = the server's word («بموافقة»). Every cell names its
 * role; the columns are the first row's cells in order (all rows carry the
 * same roles in the same order).
 */
export interface MatrixCell {
  role: string;
  role_label: string;
  value: 'yes' | 'no' | 'text';
  text?: string;
}

export interface MatrixRow {
  id: string;
  label: string;
  cells: MatrixCell[];
}

export interface PropertiesResponse {
  business_date: string;
  /** Over the properties the caller sees. */
  total: { rooms: number; pct: number };
  properties: PropertyRow[];
  shared: SharedRow[];
  matrix: MatrixRow[];
}

export const propertiesApi = {
  get: () => erpApi.get<PropertiesResponse>('/hotel/properties'),
};

export function usePropertiesQuery(opts: { enabled?: boolean } = {}) {
  const { me } = useErp();
  return useQuery({
    queryKey: hotelKeys.resource('properties', me.branch?.id ?? null),
    queryFn: propertiesApi.get,
    enabled: opts.enabled ?? true,
    placeholderData: keepPreviousData,
    retry: 1,
  });
}
